import type { NoticeDataWidgetLayer, NoticeLayer } from '@cornerstone/shared';
import { fitDataWidgetSize } from './WeatherDisplaySettings';
export type SelectionBounds = { x: number; y: number; width: number; height: number };
export type LayerAlignment = 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom' | 'horizontal-gap' | 'vertical-gap';
export type ResizeDirection = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
/** 위젯 글자는 상자 안 여유(가로·세로 중 빡빡한 쪽)를 따라간다 — 글자 비율은 유지하고 남는 쪽은 정렬로 배치된다.
 *  그래서 높이가 꽉 찬 상자를 가로로만 늘리면 글자는 그대로이고, 저장값과 실제 그려지는 크기가 어긋나지 않는다.
 *  시작 상자 대비 상대 비율이라 끌기 시작할 때 글자가 튀지 않는다. */
export function noticeWidgetFontAtSize(start: NoticeDataWidgetLayer, patch: { width?: number; height?: number }): number {
  const fit = fitDataWidgetSize(start);
  const room = (width: number, height: number) => Math.min(Math.max(1, width) / fit.width, Math.max(1, height) / fit.height);
  const ratio = room(patch.width ?? start.width, patch.height ?? start.height) / room(start.width, start.height);
  return Math.round(Math.max(8, Math.min(1000, start.fontSize * ratio)) * 100) / 100;
}
/** fromCenter(Alt): 중심을 고정하고 끈 만큼 양쪽으로 같이 늘이고 줄인다. aspectLocked(Shift·비율 고정)와 함께 쓸 수 있다. */
export function resizeBoundsFromHandle(start: SelectionBounds, dir: ResizeDirection, dx: number, dy: number, aspectLocked = false, fromCenter = false): Partial<SelectionBounds> {
  const west = ['w', 'nw', 'sw'].includes(dir), east = ['e', 'ne', 'se'].includes(dir);
  const north = ['n', 'nw', 'ne'].includes(dir), south = ['s', 'sw', 'se'].includes(dir);
  const k = fromCenter ? 2 : 1;
  let width = west ? start.width - dx * k : east ? start.width + dx * k : start.width;
  let height = north ? start.height - dy * k : south ? start.height + dy * k : start.height;
  if (aspectLocked) {
    const sx = width / Math.max(1, start.width), sy = height / Math.max(1, start.height);
    const scale = east || west ? north || south ? Math.abs(sx - 1) >= Math.abs(sy - 1) ? sx : sy : sx : sy;
    const safe = Math.max(1 / Math.max(1, start.width), 1 / Math.max(1, start.height), scale);
    width = start.width * safe; height = start.height * safe;
  }
  width = Math.max(1, Math.round(width)); height = Math.max(1, Math.round(height));
  if (fromCenter) {
    const cx = start.x + start.width / 2, cy = start.y + start.height / 2;
    return { ...(east || west || aspectLocked ? { width, x: Math.round(cx - width / 2) } : {}),
      ...(north || south || aspectLocked ? { height, y: Math.round(cy - height / 2) } : {}) };
  }
  return {
    ...(east || west ? { width } : {}), ...(north || south ? { height } : {}),
    ...(west ? { x: Math.round(start.x + start.width - width) } : {}),
    ...(north ? { y: Math.round(start.y + start.height - height) } : {}),
    ...(aspectLocked && (east || west) && !north && !south ? { height } : {}),
    ...(aspectLocked && (north || south) && !east && !west ? { width } : {}),
  };
}
// 선택 UI는 대지와 함께 scale(배율)로 줄어드는 영역 안에 그려진다. 대지 px로 크기를 주면 4K 대지에서
// 핸들이 3px대로 작아져 잡기 어려웠다 — 화면 px ÷ 배율로 잡아 해상도·줌과 상관없이 같은 크기로 보이게 한다.
export const HANDLE_VISIBLE_PX = 10, HANDLE_HIT_PX = 16;
/** 크기 조절 핸들의 잡히는 상자(부모 = 레이어 상자, 대지 px). 중심이 모서리·변 가운데에 온다. */
export function resizeHandleFrame(dir: ResizeDirection, scale: number): { left: string; top: string; size: number } {
  const size = HANDLE_HIT_PX / Math.max(0.01, scale), half = size / 2;
  const x = dir.includes('w') ? '0%' : dir.includes('e') ? '100%' : '50%';
  const y = dir.includes('n') ? '0%' : dir.includes('s') ? '100%' : '50%';
  return { left: `calc(${x} - ${half}px)`, top: `calc(${y} - ${half}px)`, size };
}
/** 미디어 레이어의 첫 배치: 원본 크기 그대로 (0,0). 캔버스보다 크면 비율을 유지한 채 캔버스 안으로만 줄인다.
 *  크기를 모르는 옛 미디어는 캔버스 전체 크기로 두고 'contain'이라 어느 쪽이든 잘리지 않는다. */
export function nativeMediaBox(media: { width: number | null; height: number | null }, canvasW: number, canvasH: number): SelectionBounds {
  const width = media.width && media.width > 0 ? media.width : canvasW, height = media.height && media.height > 0 ? media.height : canvasH;
  const shrink = Math.min(1, canvasW / width, canvasH / height);
  return { x: 0, y: 0, width: Math.max(1, Math.round(width * shrink)), height: Math.max(1, Math.round(height * shrink)) };
}
export function layerBounds(layer: NoticeLayer): SelectionBounds {
  const angle = (layer.rotation ?? 0) * Math.PI / 180;
  const width = Math.abs(layer.width * Math.cos(angle)) + Math.abs(layer.height * Math.sin(angle));
  const height = Math.abs(layer.width * Math.sin(angle)) + Math.abs(layer.height * Math.cos(angle));
  return { x: layer.x + (layer.width - width) / 2, y: layer.y + (layer.height - height) / 2, width, height };
}
export function selectionBounds(layers: NoticeLayer[]): SelectionBounds | null {
  if (!layers.length) return null;
  const boxes = layers.map(layerBounds), x = Math.min(...boxes.map(box => box.x)), y = Math.min(...boxes.map(box => box.y));
  return { x, y, width: Math.max(...boxes.map(box => box.x + box.width)) - x, height: Math.max(...boxes.map(box => box.y + box.height)) - y };
}
export function resizeNoticeLayer(layer: NoticeLayer, patch: Partial<NoticeLayer>): NoticeLayer {
  const next = { ...layer, ...patch } as NoticeLayer;
  if (patch.width !== undefined) next.width = Math.max(1, patch.width);
  if (patch.height !== undefined) next.height = Math.max(1, patch.height);
  if (layer.kind === 'dataWidget' && next.kind === 'dataWidget' && next.source?.kind === 'clock' && next.source.mode === 'analog' &&
    (layer.source?.kind !== 'clock' || layer.source.mode !== 'analog')) {
    const size = Math.min(next.width, next.height);
    next.x += (next.width - size) / 2; next.y += (next.height - size) / 2;
    next.width = size; next.height = size;
  }
  for (const key of ['x', 'y', 'width', 'height'] as const) if (patch[key] !== undefined) next[key] = Math.round(next[key] * 100) / 100;
  if (layer.kind === 'dataWidget' && next.kind === 'dataWidget' && !('fontSize' in patch) && (patch.width !== undefined || patch.height !== undefined)) {
    next.fontSize = noticeWidgetFontAtSize(layer, patch);
  }
  return next;
}
export function alignNoticeLayers(layers: NoticeLayer[], ids: string[], alignment: LayerAlignment, canvas?: SelectionBounds): NoticeLayer[] {
  const selected = layers.filter(layer => ids.includes(layer.id) && !layer.locked && !layer.hidden);
  const bounds = canvas ?? selectionBounds(selected); if (!bounds || !selected.length) return layers;
  const changes = new Map<string, Partial<NoticeLayer>>();
  if (alignment.endsWith('-gap')) {
    if (selected.length < 3) return layers;
    const horizontal = alignment === 'horizontal-gap', axis = horizontal ? 'x' : 'y', size = horizontal ? 'width' : 'height';
    const sorted = selected.map(layer => ({ layer, box: layerBounds(layer) })).sort((a, b) => a.box[axis] - b.box[axis]);
    const gap = (bounds[size] - sorted.reduce((sum, item) => sum + item.box[size], 0)) / (sorted.length - 1);
    let cursor = bounds[axis];
    for (const { layer, box } of sorted) { changes.set(layer.id, { [axis]: layer[axis] + cursor - box[axis] }); cursor += box[size] + gap; }
  } else for (const layer of selected) {
    const box = layerBounds(layer), horizontal = ['left', 'center', 'right'].includes(alignment);
    const axis = horizontal ? 'x' : 'y', size = horizontal ? 'width' : 'height';
    const factor = ['center', 'middle'].includes(alignment) ? 0.5 : ['right', 'bottom'].includes(alignment) ? 1 : 0;
    changes.set(layer.id, { [axis]: layer[axis] + bounds[axis] + (bounds[size] - box[size]) * factor - box[axis] });
  }
  return layers.map(layer => changes.has(layer.id) ? { ...layer, ...changes.get(layer.id) } as NoticeLayer : layer);
}
export function resizeNoticeSelection(layers: NoticeLayer[], ids: string[], next: SelectionBounds): NoticeLayer[] {
  const selected = layers.filter(layer => ids.includes(layer.id) && !layer.locked && !layer.hidden);
  const before = selectionBounds(selected); if (!before) return layers;
  const sx = Math.max(1, next.width) / Math.max(1, before.width), sy = Math.max(1, next.height) / Math.max(1, before.height);
  return layers.map(layer => selected.includes(layer) ? resizeNoticeLayer(layer, {
    x: next.x + (layer.x - before.x) * sx, y: next.y + (layer.y - before.y) * sy,
    width: layer.width * sx, height: layer.height * sy,
    ...(layer.kind === 'text' ? { fontSize: Math.max(1, layer.fontSize * Math.min(sx, sy)), paddingX: layer.paddingX * sx, paddingY: layer.paddingY * sy } : {}),
  }) : layer);
}
